"use client"

import type React from "react"
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from "@/components/ui/dialog"
import {Button} from "@/components/ui/button"
import {CircleX, PlusIcon, Trash2} from "lucide-react"
import {Textarea} from "@/components/ui/textarea"
import {Label} from "@/components/ui/label"
import {Checkbox} from "@/components/ui/checkbox"
import Image from "next/image"
import {SingleProductEnum} from "@/constants/commerce-enum"
import {CommentGuidelines} from "@/components/ui/add-new-comment/comment-guidelines";
import {CommentRates} from "@/components/ui/add-new-comment/comment-rates";

// Import the CommentRate interface from the main component
interface CommentRate {
    id: number
    title: string
    value: number
    color: string
    icon: any
}

interface AddNewCommentMobileProps {
    title: string
    isOpen: boolean
    onOpenChange: (open: boolean) => void
    onCloseFirstModal: () => void
    selectedOption: CommentRate
    setSelectedOption: (option: CommentRate) => void
    inputFileRef: React.RefObject<HTMLInputElement>
    viewImage: string[]
    setViewImage: React.Dispatch<React.SetStateAction<string[]>>
    handleClick: () => void
    register: any
    setValue: any
    deleteHandler: (indexImage: number) => void
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    commentRates: CommentRate[]
    handleSubmit: any
    onSubmit: (data: any) => void
}

export function AddNewCommentMobile({
                                        title,
                                        isOpen,
                                        onOpenChange,
                                        onCloseFirstModal,
                                        selectedOption,
                                        setSelectedOption,
                                        inputFileRef,
                                        viewImage,
                                        handleClick,
                                        register,
                                        setValue,
                                        deleteHandler,
                                        handleFileChange,
                                        commentRates,
                                        handleSubmit,
                                        onSubmit,
                                    }: AddNewCommentMobileProps) {
    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild className="w-full">
                <div className={"flex justify-end"}>
                    <Button
                        variant={"outline"}
                        className={"w-full md:h-14"}
                        onClick={() => {
                            onCloseFirstModal()
                            onOpenChange(true)
                        }}
                    >
                        ثبت نظر
                    </Button>
                </div>
            </DialogTrigger>
            <DialogContent className="w-full h-full overflow-y-scroll z-10">
                <DialogHeader>
                    <DialogTitle
                        className="w-full flex flex-wrap items-center justify-start text-sm">
                        <div className="w-full flex text-base flex-wrap text-[#121416] px-3">
                            {SingleProductEnum.yourComment}
                        </div>
                        <div
                            className="w-full flex text-start text-xs flex-wrap text-gray-400 pt-2 px-3">{title}</div>
                    </DialogTitle>

                    <section className="w-full flex flex-wrap text-xs content-start justify-between px-3">
                        <div className="w-full p-3 py-3">
                            {/*rating*/}
                            <CommentRates commentRates={commentRates} selectedOption={selectedOption}
                                          setSelectedOption={setSelectedOption}/>

                            <div className="w-full text-black flex flex-wrap mt-4 pb-3 border-b text-sm">
                                <div className="w-full py-2">
                                    <div className="w-full flex text-xs">{SingleProductEnum.commentContent}</div>
                                    <Textarea
                                        {...register("content", {required: true})}
                                        rows={5}
                                        className="rounded-xl border border-gray-300 my-2"
                                        dir={"rtl"}
                                        placeholder=""
                                    />
                                </div>
                            </div>

                            <div className="w-full flex-wrap mt-4 pb-3">
                                <div className={"text-sm w-full text-start mb-3"}>{SingleProductEnum.sendContent}</div>
                                <div className="w-full flex text-[#979797] items-center justify-start mb-3 text-xs">
                                    حداکثر ۳ عکس و هر کدام ۱ مگابایت با فرمت Jpeg
                                </div>
                                <div className="w-full flex flex-wrap mb-2 gap-3 mt-5">
                                    <div
                                        className="w-[120px] h-[120px] flex flex-wrap content-center items-center !border-2 rounded-xl !border-dotted border-gray-300">
                                        <div
                                            className={
                                                "w-full flex hover:text-hover-blue-secondary text-blue-secondary justify-center items-center hover:text-text"
                                            }
                                        >
                                            {SingleProductEnum.add}
                                        </div>
                                        <div className="relative w-full flex mt-5 items-center justify-center">
                                            <input
                                                disabled={viewImage?.length > 2}
                                                {...register("images")}
                                                multiple={true}
                                                ref={inputFileRef}
                                                className="hidden"
                                                type="file"
                                                onChange={handleFileChange}
                                            />
                                            <Button
                                                variant={"none"}
                                                type={"button"}
                                                className="absolute bg-white top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                                                onClick={handleClick}
                                            >
                                                <PlusIcon
                                                    className="w-4 h-4 text-orange-primary border rounded-full border-orange-primary"/>
                                            </Button>
                                        </div>
                                    </div>

                                    {viewImage &&
                                        viewImage.map((item: string, index: number) => (
                                            <div key={index} className={"relative"}>
                                                <Trash2
                                                    onClick={() => deleteHandler(index)}
                                                    className="absolute top-2 left-2 p-1 bg-gray-500 text-white !rounded-lg"
                                                />
                                                <Image
                                                    src={item || "/placeholder.svg"}
                                                    alt={""}
                                                    className={"rounded-xl my-1 object-cover w-[120px] h-[120px]"}
                                                    width={90}
                                                    height={90}
                                                />
                                            </div>
                                        ))}
                                </div>

                                <div className="w-full flex flex-wrap">
                                    <div
                                        className="w-full flex flex-wrap gap-1 text-blue-secondary mt-5 items-center text-sm">
                                        <Checkbox onCheckedChange={(e: any) => setValue("is_private", e)}
                                                  id={"is_private"}/>
                                        <Label
                                            htmlFor={"is_private"}>{SingleProductEnum.submitACommentAnonymously}</Label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/*text*/}
                        <div className="w-full p-3 md:py-5 py-3 flex flex-col">
                            {/*<CommentGuidelines mobile={true}/>*/}
                            <div className="w-full flex justify-end mt-4">
                                <Button
                                    onClick={handleSubmit(onSubmit)}
                                    className="w-full bg-orange-primary py-5 h-12"
                                >
                                    ثبت نظر
                                </Button>
                            </div>
                        </div>
                    </section>
                </DialogHeader>
            </DialogContent>
        </Dialog>
    )
}